import { doc, getDoc, type DocumentData } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type { AiAnalysisSettings, AiOutputFormat } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * settingsId から M1 (aiAnalysisSettings) → outputFormatId → M3 (aiOutputFormats) を解決し、
 * `AiOutputFormat.schema` (= 列名動的化レベル 3 の fields[] を持つ schema) を返す hook
 * (Admin Wave AW5、docs/108)。
 *
 * old2 PostsModerationPage L560-585 の outputFormat 解決 (getAiAnalysisSettings →
 * getAiOutputFormat) を admin 版に移植。apps/user の `aiFormatLoader.ts` と同設計だが、
 * apps/* 間 deep import 禁止 (ESLint boundaries) のため posts-moderation 専用に複製。
 *
 * - settingsId / outputFormatId が解決できなければ null を返す (詳細 modal は固定列に fallback)。
 * - module scope cache で同一セッション中の重複読込を防ぐ (user 版踏襲)。
 */

const _settingsCache = new Map<string, AiAnalysisSettings | null>();
const _formatCache = new Map<string, AiOutputFormat | null>();

async function loadSettings(settingsId: string): Promise<AiAnalysisSettings | null> {
  if (_settingsCache.has(settingsId)) return _settingsCache.get(settingsId) ?? null;
  try {
    const snap = await getDoc(doc(db, 'aiAnalysisSettings', settingsId));
    if (!snap.exists()) {
      _settingsCache.set(settingsId, null);
      return null;
    }
    const data = { ...(snap.data() as DocumentData), id: settingsId } as AiAnalysisSettings;
    _settingsCache.set(settingsId, data);
    return data;
  } catch {
    _settingsCache.set(settingsId, null);
    return null;
  }
}

async function loadFormat(formatId: string): Promise<AiOutputFormat | null> {
  if (_formatCache.has(formatId)) return _formatCache.get(formatId) ?? null;
  try {
    const snap = await getDoc(doc(db, 'aiOutputFormats', formatId));
    if (!snap.exists()) {
      _formatCache.set(formatId, null);
      return null;
    }
    const data = { ...(snap.data() as DocumentData), id: formatId } as AiOutputFormat;
    _formatCache.set(formatId, data);
    return data;
  } catch {
    _formatCache.set(formatId, null);
    return null;
  }
}

export function useAiOutputFormatBySettings(
  settingsId: string | null | undefined,
): AiOutputFormat | null {
  const [format, setFormat] = useState<AiOutputFormat | null>(null);

  useEffect(() => {
    if (!settingsId) {
      setFormat(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const settings = await loadSettings(settingsId);
      const formatId = settings?.outputFormatId;
      if (!formatId) {
        if (!cancelled) setFormat(null);
        return;
      }
      const fmt = await loadFormat(formatId);
      if (!cancelled) setFormat(fmt);
    })().catch(() => {
      if (!cancelled) setFormat(null);
    });
    return () => {
      cancelled = true;
    };
  }, [settingsId]);

  return format;
}
